import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { AddSectionButton, AddSectionOverlay, toneStyles } from '@/components/block-primitives';
import type { TimelineBlockConfig, TimelineEvent } from './artifact-block-types';
import EditableText from './editable-text';
type ArtifactTimelineBlockProps = {
  config: TimelineBlockConfig;
  editable?: boolean;
  onChange?: (next: TimelineBlockConfig) => void;
};
const ArtifactTimelineBlock = t0 => {
  const $ = _c(45);
  const {
    config,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const [addTitle, setAddTitle] = useState(false);
  const [addNote, setAddNote] = useState(false);
  let t2;
  if ($[0] !== config || $[1] !== onChange) {
    t2 = next => onChange?.({
      ...config,
      ...next
    });
    $[0] = config;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const patch = t2;
  let t3;
  if ($[3] !== config.events || $[4] !== patch) {
    t3 = (index, next_0) => patch({
      events: config.events.map((event, i) => i === index ? {
        ...event,
        ...next_0
      } : event)
    });
    $[3] = config.events;
    $[4] = patch;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const patchEvent = t3;
  const showTitle = !!config.title || addTitle;
  const showNote = !!config.note || addNote;
  let t4;
  if ($[6] !== editable || $[7] !== showTitle) {
    t4 = editable && !showTitle && <AddSectionOverlay position="top"><AddSectionButton label="Add a title" onClick={() => setAddTitle(true)} /></AddSectionOverlay>;
    $[6] = editable;
    $[7] = showTitle;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== editable || $[10] !== showNote) {
    t5 = editable && !showNote && <AddSectionOverlay position="bottom"><AddSectionButton label="Add a note" onClick={() => setAddNote(true)} /></AddSectionOverlay>;
    $[9] = editable;
    $[10] = showNote;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = config.title || "Timeline";
  let t7;
  if ($[12] !== addTitle || $[13] !== config.title || $[14] !== editable || $[15] !== patch || $[16] !== showTitle) {
    t7 = showTitle && <EditableText autoFocus={addTitle} block={true} className="text-label-2 text-primary" editable={editable} label="Timeline title" onSubmit={title => patch({
      title
    })} onEmptyBlur={() => setAddTitle(false)} optional={true} placeholder="Add a title" value={config.title ?? ""} />;
    $[12] = addTitle;
    $[13] = config.title;
    $[14] = editable;
    $[15] = patch;
    $[16] = showTitle;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== config.events || $[19] !== editable || $[20] !== patchEvent) {
    let t9;
    if ($[22] !== config.events.length || $[23] !== editable || $[24] !== patchEvent) {
      t9 = (event_0, index_0) => {
        const isLast = index_0 === config.events.length - 1;
        return <li className="flex gap-3" key={index_0}><div className="relative flex w-4 shrink-0 flex-col items-center"><span aria-hidden="true" className="flex h-5 items-center"><span className={cx("h-2.5 w-2.5 rounded-full", toneStyles[event_0.tone ?? "neutral"].marker)} /></span>{!isLast && <span aria-hidden="true" className="absolute bottom-0 left-1/2 top-5 w-px -translate-x-1/2 bg-senary" />}</div><div className={cx("flex min-w-0 flex-1 flex-col gap-2", !isLast && "pb-7")}><div className="flex min-h-5 flex-wrap items-baseline gap-x-2">{(editable || event_0.date) && <EditableText as="time" className="text-snippet-2 text-tertiary" editable={editable} label="Event date" onSubmit={date => patchEvent(index_0, {
                date
              })} optional={true} placeholder="Add a date" value={event_0.date ?? ""} />}<EditableText as="div" className="min-w-0 break-words text-label-2 text-primary" editable={editable} label="Event label" onSubmit={label => patchEvent(index_0, {
                label
              })} value={event_0.label} /></div>{(editable || event_0.detail) && <EditableText as="div" className="break-words text-body-2 text-tertiary" editable={editable} label="Event detail" multiline={true} onSubmit={detail => patchEvent(index_0, {
              detail
            })} optional={true} placeholder="Add detail" value={event_0.detail ?? ""} />}</div></li>;
      };
      $[22] = config.events.length;
      $[23] = editable;
      $[24] = patchEvent;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    t8 = config.events.map(t9);
    $[18] = config.events;
    $[19] = editable;
    $[20] = patchEvent;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[26] !== t8) {
    t9 = <ol className="flex flex-col">{t8}</ol>;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t7 || $[29] !== t9) {
    t10 = <div className="flex flex-col gap-4 px-4 py-4 md:px-6 md:py-5">{t7}{t9}</div>;
    $[28] = t7;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== addNote || $[32] !== config.note || $[33] !== editable || $[34] !== patch || $[35] !== showNote) {
    t11 = showNote && <div className="border-t border-tertiary bg-secondary px-4 md:px-6 py-3 text-caption-4 text-tertiary"><EditableText autoFocus={addNote} block={true} editable={editable} label="Timeline note" onSubmit={note => patch({
        note
      })} onEmptyBlur={() => setAddNote(false)} optional={true} placeholder="Add a note" value={config.note ?? ""} /></div>;
    $[31] = addNote;
    $[32] = config.note;
    $[33] = editable;
    $[34] = patch;
    $[35] = showNote;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t6) {
    t12 = <section aria-label={t6} className="overflow-hidden rounded-lg border border-tertiary bg-primary">{t10}{t11}</section>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t6;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t12 || $[42] !== t4 || $[43] !== t5) {
    t13 = <div className="group relative">{t4}{t5}{t12}</div>;
    $[41] = t12;
    $[42] = t4;
    $[43] = t5;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  return t13;
};
export default ArtifactTimelineBlock;